'use client';

import Bugsnag from '@bugsnag/js';
import { createIntl } from '@formatjs/intl';
import type { userAgent } from 'next/server';
// eslint-disable-next-line @alltrails/custom/react-imports
import React, { Fragment, PropsWithChildren } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import setGlobalFetchHeaders from '@alltrails/core/utils/setGlobalFetchHeaders';
import MapProvider from '@alltrails/maps/components/MapProvider/MapProvider';
import type { MapState } from '@alltrails/maps/redux/reducer';
import type { Bootstrap } from '@alltrails/networking/types/Bootstrap';
import type Experiments from '@alltrails/types/Experiments';
import { buildStore } from '../utils/redux';

type Props = {
  bootstrap: Bootstrap;
  browser: ReturnType<typeof userAgent>['browser'];
  experiments: Experiments;
  messages: ReturnType<typeof createIntl>['messages'];
  locale: ReturnType<typeof createIntl>['locale'];
  irclickid?: string;
  map: MapState;
};

const plugin = Bugsnag.getPlugin('react');
const ErrorBoundary = plugin ? plugin.createErrorBoundary(React) : Fragment;

export default function CustomProvider({ bootstrap, browser, experiments, messages, locale, children, irclickid, map }: PropsWithChildren<Props>) {
  const languageRegionCode = useLanguageRegionCode();
  setGlobalFetchHeaders({ languageRegionCode });
  const store = buildStore({
    algoliaConfigs: bootstrap.algoliaConfig,
    amplitudeAnalyticsSettings: bootstrap.amplitudeAnalyticsSettings,
    browser,
    context: bootstrap.context,
    currencyCode: bootstrap.currencyCode,
    experiments,
    features: bootstrap.features,
    irclickid,
    plans: bootstrap.plans,
    requestCountryCode: bootstrap.requestCountryCode,
    map
  });

  return (
    <ErrorBoundary>
      <IntlProvider messages={messages} locale={locale}>
        <Provider store={store}>
          <MapProvider>{children}</MapProvider>
        </Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
}
